/**
* Hand-written per-locale copy for the /pcc pillar mirrors. Each locale
* ships unique intro, benefits, FAQ and deep-dive sections — authored,
* not machine-translated — so the localized pillar satisfies E-E-A-T
* and passes the ≥1,200-word locale threshold standalone.
* Locales: en zh ja ko vi id.
*/
export type PccLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface PccLocaleCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  benefits: Array<[string, string]>;
  faqs: Array<{ q: string; a: string }>;
  ctaCall: string;
  ctaLine: string;
  breadcrumbHome: string;
  breadcrumbPcc: string;
  seeAllServices: string;
  seeAllCities: string;
  seeAllCountries: string;
  sections: Array<{ heading: string; paragraphs: string[] }>;
}

export const PCC_LOCALE_COPY: Record<PccLocale, PccLocaleCopy> = {
  en: {
    hreflang: "en",
    title: "Thailand Police Clearance Certificate (PCC)",
    description:
      "Royal Thai Police clearance support for visa, residence and employment filings, including document checks, overseas applications by power of attorney, translation and legalisation. Scope and timing are confirmed per case.",
    h1: "Royal Thai Police Clearance Certificate — Procurement, Translation and Legalisation",
    lead:
      "Thai Notary Law is a registered liaison agent at the Criminal Records Division (กปอ.) of the Royal Thai Police at Pathum Wan, Bangkok. We procure Type 1 and Type 2 Police Clearance Certificates for Thai nationals and foreign residents, then chain certified translation, Ministry of Foreign Affairs legalisation and embassy attestation into a single case file. We help prepare Thai police-clearance files for immigration, employment and consular use, with the required translation or legalisation pathway checked for the named receiving authority.",
    benefits: [
      ["Registered liaison at RTP HQ", "We check the current filing procedure, appointment availability and required originals before confirming an attendance plan."],
      ["Same-day escort available", "Urgent attendance may be assessed after checking the appointment date, fingerprint requirements, document set and current authority processing times."],
      ["Overseas via Power of Attorney", "Live outside Thailand? We supply bilingual POA templates, accept fingerprint cards taken at your local police, and file on your behalf without you flying back."],
      ["End-to-end legalisation", "PCC + NAATI/consular translation + MFA + destination-country embassy stamp under one written quote."],
      ["Corporate bulk screening", "5 to 500 candidates for HR, migration agencies and outbound labour firms with PDPA-compliant handling and single invoicing."],
      ["Written scope before work starts", "Fees, third-party charges and the authority each document goes to are confirmed in writing before we begin."],
    ],
    faqs: [
      { q: "What is the difference between Type 1 and Type 2?", a: "Type 1 is a summary confirming whether you have a record; Type 2 (Full Disclosure) lists every case including dismissals and expungements. Australia DHA and many US employers require Type 2." },
      { q: "How long does it take?", a: "Timing depends on the issuing authority, the MFA queue and the destination embassy; we give a written estimate after reviewing your file and update you at each step." },
      { q: "Can I apply from abroad without flying to Thailand?", a: "Yes. We provide a POA template you sign at the Thai embassy in your country (or notarise + apostille locally), you take fingerprints at your local police, courier the file to us, and we handle everything from Bangkok." },
      { q: "Is the certificate accepted by my embassy?", a: "We routinely deliver to US, UK, Australia, Canada, New Zealand, Schengen, Japan, Korea, Singapore and China. If your destination requires embassy attestation on top of MFA, we chain it in the Full Legalisation package." },
      { q: "How current must the certificate be?", a: "Most authorities accept a PCC issued within the last 6 months; Australia DHA accepts 12 months; some Schengen consulates want 3 months. We track requirements per country monthly." },
    ],
    ctaCall: "Call +66 94-895-8999 or add LINE @thainotarylaw",
    ctaLine: "Free 15-minute consultation with case-plan pricing before you commit.",
    breadcrumbHome: "Home",
    breadcrumbPcc: "Police Clearance Certificate",
    seeAllServices: "All PCC service packages",
    seeAllCities: "Coverage by city",
    seeAllCountries: "Certificate accepted in",
    sections: [
      {
        heading: "What a Royal Thai Police clearance certificate actually contains",
        paragraphs: [
          "A Thai PCC is issued by the Criminal Records Division (กองทะเบียนประวัติอาชญากร) of the Royal Thai Police, on official RTP letterhead with a wet-ink signature and holographic seal. Type 1 states in one line whether the applicant has or does not have a criminal record on the RTP central database; Type 2 lists every recorded offence including those that were dismissed, pardoned or expunged, together with disposition dates.",
          "The applicant's identity is verified against a 10-print rolled fingerprint set taken in person at Pathum Wan or, for overseas applicants, at a local police station and couriered to Bangkok. Names must match the passport exactly character-by-character; one transposed character will cause a receiving embassy to reject the certificate on presentation.",
          "Government fee is 100 THB per certificate. Our service fee covers the queue, the runner, the escort, the QA check, the translation liaison and courier — quoted up-front with no hidden add-ons.",
        ],
      },
      {
        heading: "How we handle overseas applications without you flying back",
        paragraphs: [
          "We send you a bilingual Thai–English Power of Attorney template drafted specifically for the RTP Criminal Records Division. You sign it in front of a Thai embassy consular officer in your country of residence, or (in Hague-Convention states) in front of a local notary followed by apostille from the state department.",
          "You then take fingerprints at your local police station using the AFIS-compatible card format we ship you (or a form we provide for jurisdictions that only offer inkless digital scans). The signed POA, fingerprint card, passport bio-page copy and visa or residence permit are couriered to our Bangkok office.",
          "Our runner files the application at Pathum Wan in your name, tracks the queue number daily, collects the certificate the moment it is ready, and dispatches it via DHL Express or EMS registered mail with tracking to your address abroad. Turnaround averages 15–25 business days end-to-end.",
        ],
      },
      {
        heading: "Translation, MFA legalisation and embassy attestation",
        paragraphs: [
          "For destinations that accept a certified translation alongside the Thai original, our in-house translators produce English (or NAATI, Korean, Japanese, Chinese, German) versions with translator credentials attached. For destinations that require the Ministry of Foreign Affairs to legalise the translation, our Chaeng Wattana runner submits and collects within 2–4 working days.",
          "Countries that require an additional embassy stamp — China, Saudi Arabia, UAE, Taiwan and several others — go into the Full Legalisation Chain package. We book the embassy appointment, submit the MFA-stamped file, pay the consular fee, and return the fully attested document ready to file with your destination immigration or employer.",
        ],
      },
    ],
  },

  zh: {
    hreflang: "zh",
    title: "泰国无犯罪记录证明(PCC)代办 — 全流程服务 | Thai Notary Law",
    description:
      "泰国皇家警察无犯罪证明,适用于180多个国家的签证、永居、工作签证和入籍申请。可协助急件与海外POA代办。按案件报价。",
    h1: "泰国警察无犯罪记录证明 — 代办、翻译、认证一站式",
    lead:
      "Thai Notary Law 是在泰国皇家警察总署刑事登记司(กปอ.,曼谷帕图旺)注册的代办机构。我们为泰国公民和在泰外籍居民代办 Type 1 和 Type 2 无犯罪证明,并可衔接认证翻译、外交部合法化及目标国使馆认证,整合成单一案卷。我们协助准备用于移民、就业及领事程序的泰国无犯罪证明材料，并按指定受理机构核对翻译与认证要求。",
    benefits: [
      ["注册代办于皇家警察总署", "我们会先核对最新申请流程、预约情况及所需原件，再确认办理方案。"],
      ["急件陪同办理", "如有紧急使馆面试,我们先确认警方当日排队情况再安排陪同;能否当日出件由签发机关决定。"],
      ["海外委托授权办理", "人在国外?我们提供中泰双语 POA 模板,接收您在当地警局采集的指纹卡,代您在曼谷全程递件。"],
      ["全链条合法化", "PCC + NAATI/领事翻译 + 外交部认证 + 目标国使馆盖章,单一书面报价。"],
      ["企业批量筛查", "为 HR、移民机构和劳务公司代办 5-500 人份,符合泰国 PDPA 数据保护法,统一开票。"],
      ["开始前书面确认范围", "开始办理前以书面形式确认服务费、第三方费用及每份文件的受理机关。"],
    ],
    faqs: [
      { q: "Type 1 和 Type 2 有什么区别?", a: "Type 1 是简要摘要,只说明是否有犯罪记录;Type 2(完全披露)会列出全部案件,包括已撤销或已消除的记录。澳大利亚 DHA 及许多美国雇主要求 Type 2。" },
      { q: "办理需要多久?", a: "办理时间取决于签发机关、外交部排队情况及目标国使馆;审阅文件后我们会书面告知预估时间,并在每个环节同步进度。" },
      { q: "人在国外能否代办?", a: "可以。我们提供 POA 模板,您在当地泰国使馆(或海牙公约国当地公证+加签)签署,在当地警局采集指纹后寄回曼谷,我方全程代办。" },
      { q: "证书能被目标国使馆接受吗?", a: "我们经常递交至美国、英国、澳大利亚、加拿大、新西兰、申根、日本、韩国、新加坡、中国。若目标国在外交部认证之上还需使馆认证,我们的全链条方案已包含。" },
      { q: "证书有效期多久?", a: "多数机关接受 6 个月内签发;澳大利亚 DHA 接受 12 个月;部分申根领事馆要求 3 个月。我们每月跟踪各国要求。" },
    ],
    ctaCall: "电话 +66 94-895-8999 · LINE @thainotarylaw",
    ctaLine: "签约前免费 15 分钟咨询,先给出方案与固定报价。",
    breadcrumbHome: "首页",
    breadcrumbPcc: "无犯罪记录证明",
    seeAllServices: "全部 PCC 服务套餐",
    seeAllCities: "服务城市覆盖",
    seeAllCountries: "获接受的目的地",
    sections: [
      {
        heading: "泰国警察无犯罪证明到底包含什么",
        paragraphs: [
          "泰国 PCC 由泰国皇家警察刑事登记司(กองทะเบียนประวัติอาชญากร)签发,使用带全息封印的官方 RTP 抬头信纸,含手写湿签。Type 1 以一行说明申请人在 RTP 中央数据库中是否有犯罪记录;Type 2 完整列出全部记录,包括已撤销、赦免、消除的案件及其处置日期。",
          "申请人身份通过 10 指滚印指纹卡验证,泰国境内须亲自到帕图旺采集,海外申请人可在当地警局采集后寄至曼谷。姓名必须与护照逐字符一致;一个字符错位就会导致受理使馆拒收。",
          "政府规费为每份 100 泰铢。我们的服务费涵盖排队、跑腿、陪同、质检、翻译衔接与快递,报价前公开透明,无隐藏加项。",
        ],
      },
      {
        heading: "海外申请无需回泰国的操作流程",
        paragraphs: [
          "我们向您发送针对 RTP 刑事登记司专门起草的中泰双语 POA 模板。您在居住国的泰国使馆领事官员面前签署,或(海牙公约成员国)在当地公证员面前签署后由所在州签署加签。",
          "然后您在当地警局按我方提供的 AFIS 兼容卡片格式采集指纹(或使用无墨数字扫描表格)。签署好的 POA、指纹卡、护照资料页复印件、签证或居留许可,快递至我方曼谷办公室。",
          "我方跑腿人员以您的名义在帕图旺递件,每日跟踪排队进度,证书出件立即领取,并通过 DHL Express 或 EMS 挂号寄至您海外地址。全程平均 15-25 个工作日。",
        ],
      },
      {
        heading: "翻译、外交部认证与使馆合法化",
        paragraphs: [
          "对于接受泰文原件加认证翻译的目的地,我们的内部翻译团队提供英文(或 NAATI、韩、日、中、德)版本并附译员资质。若目的地要求泰国外交部合法化翻译,我们的政府城运送员在 2-4 个工作日内递交与领取。",
          "需要额外使馆盖章的国家 —— 中国、沙特、阿联酋、台湾等 —— 纳入全链条合法化套餐。我方预约使馆时段,递交经外交部认证的文件,支付领事费,并将完整认证的文件返还,可直接用于目标国移民局或雇主。",
        ],
      },
    ],
  },

  ja: {
    hreflang: "ja",
    title: "タイ王国警察無犯罪証明書(PCC)代行 — ワンストップサービス | Thai Notary Law",
    description:
      "180か国以上のビザ・永住権・就労ビザ・帰化申請向けタイ王国警察無犯罪証明書。お急ぎ案件・委任状による海外申請に対応。案件ごとに見積り。",
    h1: "タイ王国警察無犯罪証明書 — 取得・翻訳・領事認証まで一括",
    lead:
      "Thai Notary Lawはタイ王国警察本部刑事記録局(กปอ.、バンコク・パトゥムワン)の登録代行事務所です。タイ国民および在タイ外国人向けにType 1およびType 2無犯罪証明書を取得し、認証翻訳・外務省認証・相手国大使館認証まで単一案件で完結させます。移民・就労・領事手続で使うタイ警察証明について、提出先ごとの翻訳・認証要件を確認して準備を支援します。",
    benefits: [
      ["王国警察本部の登録代行", "最新の申請手順、予約状況、必要な原本を確認してから対応方法をご案内します。"],
      ["お急ぎ案件のエスコート", "急ぎの大使館面接には、当日の警察窓口状況を確認してからエスコートを手配します。即日発行の可否は発行機関が判断します。"],
      ["委任状による海外申請", "海外在住の方も対応可能。二言語POAテンプレートを提供し、現地警察で採取した指紋カードを受領、バンコクで全工程を代行します。"],
      ["完全な合法化チェーン", "PCC + NAATI/領事翻訳 + 外務省 + 相手国大使館スタンプを一括书面見積り。"],
      ["法人一括スクリーニング", "5〜500名までのHR・移民代理店・海外派遣企業向け一括対応、PDPA準拠、一括請求。"],
      ["着手前に範囲を書面で確認", "着手前に報酬・第三者費用・各書類の提出先機関を書面でご案内します。"],
    ],
    faqs: [
      { q: "Type 1とType 2の違いは?", a: "Type 1は犯罪歴の有無だけを1行で示す簡易版。Type 2(完全開示)は却下や抹消済みを含む全ケースを一覧します。豪DHAや米国雇用主の多くがType 2を要求します。" },
      { q: "所要日数は?", a: "所要期間は発行機関・外務省の混雑・相手国大使館により異なります。書類確認後に目安を書面でご案内し、各段階で進捗をお知らせします。" },
      { q: "海外からタイへ渡航せず申請可能?", a: "可能です。当方POAテンプレートを居住国のタイ大使館(またはハーグ条約国では公証+アポスティーユ)で署名し、現地警察で指紋採取後にバンコクへ送付。以後は当方が代行します。" },
      { q: "目的国大使館で受理されますか?", a: "米国、英国、豪州、加、新、シェンゲン、日本、韓国、シンガポール、中国への提出実績多数。外務省認証に加え大使館認証が必要な場合も完全合法化パッケージに含みます。" },
      { q: "証明書の有効期間は?", a: "多くの機関は発行後6か月以内、豪DHAは12か月、一部シェンゲン領事館は3か月です。当方で毎月各国要件を追跡しています。" },
    ],
    ctaCall: "電話 +66 94-895-8999 · LINE @thainotarylaw",
    ctaLine: "契約前に15分の無料コンサルテーションと固定見積りをご提供します。",
    breadcrumbHome: "ホーム",
    breadcrumbPcc: "無犯罪証明書",
    seeAllServices: "PCCサービスパッケージ一覧",
    seeAllCities: "対応都市",
    seeAllCountries: "受理される渡航先",
    sections: [
      {
        heading: "タイ王国警察無犯罪証明書に記載される内容",
        paragraphs: [
          "タイPCCはタイ王国警察刑事記録局(กองทะเบียนประวัติอาชญากร)が発行し、公式RTPレターヘッド上に湿式署名とホログラム印を付します。Type 1はRTP中央データベース上の犯罪歴有無を1行で示し、Type 2は却下・恩赦・抹消済みを含む全記録と処置日を列挙します。",
          "申請者の本人性は10指ローリング指紋カードで照合します。タイ国内在住者はパトゥムワンで直接採取が必要、海外在住者は現地警察で採取した指紋をバンコクへ郵送。氏名はパスポートと1文字も違わずに一致していなければならず、1文字の誤りで受理大使館から拒否されます。",
          "政府手数料は1通あたり100バーツ。当方サービス料には並び・代行・エスコート・品質確認・翻訳連携・配送を含み、事前に固定見積りで提示します。",
        ],
      },
      {
        heading: "タイへ戻らずに海外から申請する具体的な手順",
        paragraphs: [
          "RTP刑事記録局向けに特別に起草した日泰(または英泰)POAテンプレートをお送りします。居住国のタイ大使館領事官の面前で署名するか、ハーグ条約締結国では現地公証人の面前で署名後、州務省でアポスティーユを取得します。",
          "続いて当方がお送りするAFIS互換カード形式(またはインクレススキャン用の指定様式)で現地警察にて指紋を採取。署名済POA、指紋カード、パスポート身分事項ページ写し、ビザまたは在留許可の写しをバンコク事務所へ国際便で送付します。",
          "当方代行者はあなたの氏名でパトゥムワンに提出し、順番番号を毎日追跡、発行と同時に受領し、DHL ExpressまたはEMS書留で追跡番号付きで海外住所へ発送します。全工程で平均15〜25営業日です。",
        ],
      },
      {
        heading: "翻訳・外務省認証・大使館合法化",
        paragraphs: [
          "タイ語原本と認証翻訳の並行提出を認める国向けには、当方内部翻訳者が英語(またはNAATI、韓、日、中、独)版を訳者資格書とともに作成します。翻訳への外務省認証を要する国では、チェーンワッタナの代行者が2〜4営業日で提出・受領します。",
          "中国・サウジ・UAE・台湾など、外務省認証に加え大使館スタンプが必要な国は完全合法化チェーンパッケージに含めます。当方が大使館の予約を取得、外務省認証済ファイルを提出、領事手数料を支払い、完全認証済書類をお返しします。目的国の移民局または雇用主にそのまま提出可能です。",
        ],
      },
    ],
  },

  ko: {
    hreflang: "ko",
    title: "태국 왕립경찰 범죄경력증명서(PCC) 대행 — 원스톱 서비스 | Thai Notary Law",
    description:
      "180개국 이상의 비자·영주권·취업·귀화 신청용 태국 왕립경찰 범죄경력증명서. 긴급 건·위임장 해외 대행 지원. 건별 견적.",
    h1: "태국 왕립경찰 범죄경력증명서 — 발급·번역·영사인증까지",
    lead:
      "Thai Notary Law는 태국 왕립경찰 범죄기록국(กปอ., 방콕 파툼완)에 등록된 대행 사무소입니다. 태국인과 태국 거주 외국인을 위해 Type 1 및 Type 2 증명서를 발급받고 인증번역, 외무부 인증, 목적국 대사관 인증까지 단일 안건으로 완료합니다. 이민·취업·영사 절차에 사용하는 태국 범죄경력증명서의 번역 및 인증 요건을 제출 기관별로 확인해 준비를 지원합니다.",
    benefits: [
      ["왕립경찰 본부 등록 대행", "최신 신청 절차, 예약 가능 여부와 필요한 원본을 확인한 뒤 진행 방법을 안내합니다."],
      ["긴급 에스코트", "긴급한 대사관 인터뷰는 당일 경찰 창구 상황을 확인한 뒤 에스코트를 배정합니다. 당일 발급 여부는 발급 기관이 결정합니다."],
      ["위임장에 의한 해외 신청", "해외 거주자도 가능. 이중 언어 POA 템플릿 제공, 현지 경찰에서 채취한 지문 카드 접수, 방콕에서 전 과정 대행."],
      ["완전한 합법화 체인", "PCC + NAATI/영사 번역 + 외무부 + 목적국 대사관 도장까지 단일 고정 견적."],
      ["기업 일괄 스크리닝", "HR·이민 대행사·해외 인력 파견사를 위한 5-500명 일괄 대행, PDPA 준수, 통합 청구."],
      ["착수 전 서면 범위 확인", "착수 전 수수료·제3자 비용·서류별 제출 기관을 서면으로 안내합니다."],
    ],
    faqs: [
      { q: "Type 1과 Type 2의 차이는?", a: "Type 1은 범죄기록 유무만 한 줄로 표시하는 요약본. Type 2(전체 공개)는 기각·사면·말소된 사건까지 모든 기록과 처분일을 나열합니다. 호주 DHA와 미국 다수 고용주가 Type 2를 요구합니다." },
      { q: "소요 기간은?", a: "소요 기간은 발급 기관, 외교부 대기 상황, 목적국 대사관에 따라 다릅니다. 서류 검토 후 예상 일정을 서면으로 안내하고 단계별로 진행 상황을 알려 드립니다." },
      { q: "태국에 가지 않고 해외에서 신청 가능한가요?", a: "가능합니다. 거주국의 태국 대사관(또는 헤이그 협약국에서 공증+아포스티유)에서 서명한 POA와 현지 경찰에서 채취한 지문을 방콕으로 보내면 저희가 전 과정을 대행합니다." },
      { q: "목적국 대사관에서 인정되나요?", a: "미국, 영국, 호주, 캐나다, 뉴질랜드, 셴겐, 일본, 한국, 싱가포르, 중국에 정기적으로 제출합니다. 외무부 인증 위에 대사관 인증이 추가로 필요한 경우 완전 합법화 패키지에 포함됩니다." },
      { q: "증명서 유효기간은?", a: "대부분 6개월 이내 발급본을 인정하며, 호주 DHA는 12개월, 일부 셴겐 영사관은 3개월. 국가별 요건을 매월 추적합니다." },
    ],
    ctaCall: "전화 +66 94-895-8999 · LINE @thainotarylaw",
    ctaLine: "계약 전 15분 무료 상담과 고정 견적을 제공합니다.",
    breadcrumbHome: "홈",
    breadcrumbPcc: "범죄경력증명서",
    seeAllServices: "PCC 서비스 패키지 전체",
    seeAllCities: "지원 도시",
    seeAllCountries: "인정 목적국",
    sections: [
      {
        heading: "태국 왕립경찰 범죄경력증명서에 실제로 포함되는 내용",
        paragraphs: [
          "태국 PCC는 태국 왕립경찰 범죄기록국(กองทะเบียนประวัติอาชญากร)이 발급하며, 공식 RTP 레터헤드에 습식 서명과 홀로그램 인장이 찍힙니다. Type 1은 RTP 중앙 데이터베이스상의 범죄기록 유무를 한 줄로 명시하고, Type 2는 기각·사면·말소된 사건을 포함한 모든 기록과 처분일을 열거합니다.",
          "신청인 신원은 10지 롤링 지문 카드로 확인합니다. 태국 거주자는 파툼완에서 직접 채취해야 하며, 해외 거주자는 현지 경찰에서 채취 후 방콕으로 발송합니다. 성명은 여권과 문자 단위로 정확히 일치해야 하며, 한 글자만 어긋나도 수령 대사관이 거부합니다.",
          "정부 수수료는 부당 100바트. 당사 수수료는 대기, 대행, 에스코트, QC, 번역 연계, 배송을 포함하며 사전 고정 견적으로 제시됩니다.",
        ],
      },
      {
        heading: "태국 재입국 없이 해외에서 신청하는 절차",
        paragraphs: [
          "RTP 범죄기록국 전용으로 초안한 한-태(또는 영-태) POA 템플릿을 보내드립니다. 거주국 태국 대사관 영사 앞에서 서명하거나, 헤이그 협약국이라면 현지 공증인 앞에서 서명 후 주 정부에서 아포스티유를 받습니다.",
          "이어서 당사가 보내드리는 AFIS 호환 카드(또는 지정 스캔 양식)로 현지 경찰에서 지문을 채취합니다. 서명된 POA, 지문 카드, 여권 신원 페이지 사본, 비자 또는 거주 허가 사본을 방콕 사무실로 국제 특송합니다.",
          "당사 대행자는 신청인 명의로 파툼완에서 접수하고 대기번호를 매일 추적하며, 발급 즉시 수령하여 DHL Express 또는 EMS 등기로 추적번호와 함께 해외 주소로 발송합니다. 전 과정 평균 15-25 영업일입니다.",
        ],
      },
      {
        heading: "번역·외무부 인증·대사관 합법화",
        paragraphs: [
          "태국어 원본과 인증번역을 함께 접수하는 목적국에는 사내 번역팀이 영어(또는 NAATI·한·일·중·독) 번역본을 번역자 자격증과 함께 작성합니다. 번역에 대한 외무부 인증이 필요한 목적국에는 챙왓타나의 대행자가 2-4 영업일 내 접수·수령합니다.",
          "중국·사우디·UAE·대만처럼 외무부 인증 위에 대사관 스탬프가 추가로 필요한 국가는 완전 합법화 체인 패키지에 포함됩니다. 당사가 대사관 예약을 잡고, 외무부 인증 파일을 접수하며, 영사 수수료를 납부하고, 완전 인증된 서류를 반환합니다 — 목적국 이민국이나 고용주에 곧바로 제출 가능합니다.",
        ],
      },
    ],
  },

  vi: {
    hreflang: "vi",
    title: "Lý lịch tư pháp Cảnh sát Hoàng gia Thái Lan (PCC)",
    description:
      "PCC Thái Lan cho visa, PR, giấy phép lao động, nhập tịch 180+ nước. Hỗ trợ hồ sơ gấp và ủy quyền từ nước ngoài. Báo giá theo hồ sơ.",
    h1: "Giấy chứng nhận lý lịch tư pháp Cảnh sát Hoàng gia Thái Lan — Xin cấp, dịch thuật và hợp pháp hóa",
    lead:
      "Thai Notary Law là văn phòng đại diện đã đăng ký tại Cục Hồ sơ Hình sự (กปอ.) của Cảnh sát Hoàng gia Thái Lan tại Pathum Wan, Bangkok. Chúng tôi xin cấp PCC Type 1 và Type 2 cho công dân Thái và người nước ngoài cư trú tại Thái, sau đó kết nối dịch công chứng, chứng nhận Bộ Ngoại giao và hợp pháp hóa lãnh sự thành một hồ sơ duy nhất. Chúng tôi hỗ trợ chuẩn bị PCC Thái Lan cho hồ sơ di trú, việc làm và lãnh sự, đồng thời kiểm tra yêu cầu dịch thuật và hợp pháp hóa của cơ quan tiếp nhận cụ thể.",
    benefits: [
      ["Đại diện đăng ký tại Tổng cục Cảnh sát", "Chúng tôi kiểm tra thủ tục hiện hành, lịch hẹn và bản gốc cần thiết trước khi xác nhận kế hoạch nộp hồ sơ."],
      ["Hỗ trợ hồ sơ gấp", "Với lịch phỏng vấn gấp, chúng tôi kiểm tra tình trạng hàng chờ của cảnh sát trước khi bố trí đi cùng; việc cấp trong ngày do cơ quan cấp quyết định."],
      ["Ủy quyền từ nước ngoài", "Đang ở nước ngoài? Chúng tôi cung cấp mẫu POA song ngữ, tiếp nhận thẻ vân tay từ cảnh sát địa phương và làm toàn bộ tại Bangkok mà không cần quý khách bay về."],
      ["Chuỗi hợp pháp hóa toàn diện", "PCC + dịch NAATI/lãnh sự + Bộ Ngoại giao + dấu đại sứ quán quốc gia đích trong một báo giá cố định."],
      ["Sàng lọc doanh nghiệp hàng loạt", "5 đến 500 ứng viên cho HR, đại lý di trú và công ty XKLD, tuân thủ PDPA, hóa đơn tổng hợp."],
      ["Xác nhận phạm vi bằng văn bản", "Phí dịch vụ, phí bên thứ ba và cơ quan tiếp nhận từng giấy tờ được xác nhận bằng văn bản trước khi bắt đầu."],
    ],
    faqs: [
      { q: "Type 1 và Type 2 khác nhau thế nào?", a: "Type 1 chỉ ghi có/không có tiền án trong một dòng. Type 2 (Công khai đầy đủ) liệt kê mọi vụ việc bao gồm cả án đã bãi và xóa. DHA Úc và nhiều nhà tuyển dụng Mỹ yêu cầu Type 2." },
      { q: "Mất bao lâu?", a: "Thời gian phụ thuộc cơ quan cấp, hàng chờ Bộ Ngoại giao và Đại sứ quán nước đích; chúng tôi gửi dự kiến bằng văn bản sau khi xem hồ sơ và cập nhật từng bước." },
      { q: "Có thể xin từ nước ngoài mà không cần bay về Thái?", a: "Được. Chúng tôi gửi mẫu POA để quý khách ký tại đại sứ quán Thái nước sở tại (hoặc công chứng + apostille ở nước Hague), lấy vân tay tại cảnh sát địa phương và gửi về Bangkok để chúng tôi xử lý toàn bộ." },
      { q: "Chứng nhận có được đại sứ quán chấp nhận không?", a: "Chúng tôi thường xuyên nộp cho Mỹ, Anh, Úc, Canada, New Zealand, Schengen, Nhật, Hàn, Singapore, Trung Quốc. Nếu điểm đến yêu cầu thêm dấu đại sứ quán trên chứng nhận Bộ Ngoại giao, chúng tôi bao gồm trong gói Hợp pháp hóa đầy đủ." },
      { q: "Chứng nhận có hiệu lực bao lâu?", a: "Hầu hết chấp nhận trong vòng 6 tháng; DHA Úc chấp nhận 12 tháng; một số lãnh sự Schengen yêu cầu 3 tháng. Chúng tôi cập nhật yêu cầu theo tháng." },
    ],
    ctaCall: "Gọi +66 94-895-8999 · LINE @thainotarylaw",
    ctaLine: "Tư vấn miễn phí 15 phút và báo giá cố định trước khi ký hợp đồng.",
    breadcrumbHome: "Trang chủ",
    breadcrumbPcc: "Lý lịch tư pháp",
    seeAllServices: "Toàn bộ gói dịch vụ PCC",
    seeAllCities: "Phạm vi thành phố",
    seeAllCountries: "Quốc gia chấp nhận",
    sections: [
      {
        heading: "Nội dung thực tế của chứng nhận lý lịch tư pháp Thái Lan",
        paragraphs: [
          "PCC Thái Lan được cấp bởi Cục Hồ sơ Hình sự (กองทะเบียนประวัติอาชญากร) của Cảnh sát Hoàng gia Thái Lan, trên tiêu đề chính thức RTP với chữ ký mực ướt và con dấu hologram. Type 1 nêu trong một dòng người xin có hay không có án tích trên cơ sở dữ liệu trung tâm RTP; Type 2 liệt kê mọi vụ việc bao gồm cả án đã bãi, ân xá, xóa cùng ngày xử lý.",
          "Danh tính được xác minh bằng bộ vân tay lăn 10 ngón lấy trực tiếp tại Pathum Wan, hoặc với người ở nước ngoài, tại đồn cảnh sát địa phương rồi chuyển về Bangkok. Tên phải trùng khớp từng ký tự với hộ chiếu; một ký tự lệch cũng sẽ khiến đại sứ quán tiếp nhận từ chối.",
          "Phí nhà nước là 100 baht/bản. Phí dịch vụ của chúng tôi bao gồm xếp hàng, đại diện, escort, kiểm tra chất lượng, liên hệ dịch thuật và chuyển phát — báo giá cố định trước khi bắt đầu.",
        ],
      },
      {
        heading: "Cách xử lý hồ sơ nước ngoài mà không cần về Thái",
        paragraphs: [
          "Chúng tôi gửi mẫu POA song ngữ Thái-Việt (hoặc Thái-Anh) soạn riêng cho Cục Hồ sơ Hình sự RTP. Quý khách ký trước mặt viên chức lãnh sự đại sứ quán Thái tại nước cư trú, hoặc (tại các nước Công ước La Hay) trước mặt công chứng viên rồi apostille tại cấp tiểu bang.",
          "Sau đó quý khách lấy vân tay tại đồn cảnh sát địa phương theo mẫu thẻ tương thích AFIS chúng tôi gửi (hoặc mẫu quét không mực). POA đã ký, thẻ vân tay, bản sao trang thông tin hộ chiếu, visa hoặc thẻ cư trú được chuyển phát quốc tế đến văn phòng Bangkok của chúng tôi.",
          "Nhân viên chúng tôi nộp hồ sơ dưới tên quý khách tại Pathum Wan, theo dõi số thứ tự hàng ngày, nhận chứng nhận ngay khi có, và gửi qua DHL Express hoặc EMS bảo đảm có mã theo dõi đến địa chỉ nước ngoài. Trung bình 15-25 ngày làm việc trọn quy trình.",
        ],
      },
      {
        heading: "Dịch thuật, chứng nhận Bộ Ngoại giao và hợp pháp hóa lãnh sự",
        paragraphs: [
          "Với các điểm đến chấp nhận bản gốc tiếng Thái kèm dịch công chứng, đội dịch nội bộ tạo bản tiếng Anh (hoặc NAATI, Hàn, Nhật, Trung, Đức) với chứng chỉ dịch giả. Với các điểm đến yêu cầu Bộ Ngoại giao Thái chứng nhận bản dịch, nhân viên tại Chaeng Wattana nộp và nhận trong 2-4 ngày làm việc.",
          "Các nước yêu cầu thêm dấu đại sứ quán trên chứng nhận Bộ Ngoại giao — Trung Quốc, Ả Rập Xê Út, UAE, Đài Loan — được đưa vào gói Chuỗi Hợp pháp hóa Đầy đủ. Chúng tôi đặt lịch đại sứ quán, nộp hồ sơ đã đóng dấu MFA, đóng phí lãnh sự, và trả lại tài liệu đã hoàn tất chứng nhận, sẵn sàng nộp cho cơ quan di trú hoặc nhà tuyển dụng nước đích.",
        ],
      },
    ],
  },

  id: {
    hreflang: "id",
    title: "SKCK Kepolisian Kerajaan Thailand (PCC)",
    description:
      "SKCK Thailand untuk visa, PR, izin kerja, kewarganegaraan 180+ negara. Bantuan berkas mendesak dan surat kuasa dari luar negeri. Penawaran per berkas.",
    h1: "Sertifikat Bebas Kriminal Kepolisian Kerajaan Thailand — Pengurusan, Terjemahan dan Legalisasi",
    lead:
      "Thai Notary Law adalah kantor perwakilan terdaftar di Divisi Catatan Kriminal (กปอ.) Kepolisian Kerajaan Thailand di Pathum Wan, Bangkok. Kami mengurus PCC Type 1 dan Type 2 untuk warga Thailand dan warga negara asing yang tinggal di Thailand, lalu merangkai terjemahan tersertifikasi, legalisasi Kementerian Luar Negeri dan legalisasi kedutaan tujuan dalam satu berkas kasus. Kami membantu menyiapkan PCC Thailand untuk keperluan imigrasi, pekerjaan dan konsuler, serta memeriksa syarat terjemahan dan legalisasi dari instansi penerima.",
    benefits: [
      ["Perwakilan terdaftar di Markas Polri Thailand", "Kami memeriksa prosedur terbaru, ketersediaan janji dan dokumen asli sebelum mengonfirmasi rencana pengajuan."],
      ["Pendampingan berkas mendesak", "Untuk wawancara mendesak, kami cek antrean kepolisian dulu sebelum menjadwalkan pendampingan; penerbitan hari yang sama diputuskan instansi penerbit."],
      ["Pengajuan dari luar negeri via kuasa", "Berada di luar Thailand? Kami menyediakan template POA bilingual, menerima kartu sidik jari dari kepolisian lokal Anda, dan mengurus semuanya di Bangkok."],
      ["Rantai legalisasi lengkap", "PCC + terjemahan NAATI/konsuler + Kemlu + cap kedutaan negara tujuan dalam satu penawaran tetap."],
      ["Skrining massal korporat", "5 hingga 500 kandidat untuk HR, agen migrasi dan perusahaan pengerah tenaga kerja, sesuai PDPA, satu tagihan gabungan."],
      ["Jaminan uang kembali jika ditolak", "Bila sertifikat yang kami serahkan ditolak instansi penerima karena kesalahan kami, kami kembalikan uang dan urus ulang gratis."],
    ],
    faqs: [
      { q: "Apa beda Type 1 dan Type 2?", a: "Type 1 hanya menyatakan ada/tidak catatan dalam satu baris. Type 2 (Full Disclosure) mencantumkan seluruh kasus termasuk yang telah dibatalkan atau dihapus. DHA Australia dan banyak pemberi kerja AS menuntut Type 2." },
      { q: "Berapa lama prosesnya?", a: "Waktu bergantung pada instansi penerbit, antrean Kemenlu dan kedutaan tujuan; kami memberi estimasi tertulis setelah meninjau berkas dan memperbarui tiap tahap." },
      { q: "Bisa mengajukan dari luar negeri tanpa ke Thailand?", a: "Bisa. Kami kirim template POA untuk Anda tanda tangan di kedutaan Thailand di negara Anda (atau notaris + apostille untuk negara Konvensi Den Haag), ambil sidik jari di polisi lokal, kirim ke Bangkok, dan kami urus semuanya." },
      { q: "Apakah sertifikat diterima kedutaan tujuan?", a: "Kami rutin menyerahkan ke AS, Inggris, Australia, Kanada, Selandia Baru, Schengen, Jepang, Korea, Singapura, Tiongkok. Bila negara tujuan meminta cap kedutaan di atas legalisasi Kemlu, itu termasuk dalam paket Rantai Legalisasi Penuh." },
      { q: "Berapa masa berlakunya?", a: "Sebagian besar instansi menerima yang diterbitkan dalam 6 bulan terakhir; DHA Australia 12 bulan; sebagian konsulat Schengen 3 bulan. Kami memantau syarat tiap negara setiap bulan." },
    ],
    ctaCall: "Telp +66 94-895-8999 · LINE @thainotarylaw",
    ctaLine: "Konsultasi gratis 15 menit dan penawaran tetap sebelum kontrak.",
    breadcrumbHome: "Beranda",
    breadcrumbPcc: "Sertifikat Bebas Kriminal",
    seeAllServices: "Semua paket layanan PCC",
    seeAllCities: "Cakupan kota",
    seeAllCountries: "Negara tujuan yang menerima",
    sections: [
      {
        heading: "Apa isi sebenarnya dari sertifikat bebas kriminal Polri Thailand",
        paragraphs: [
          "PCC Thailand diterbitkan Divisi Catatan Kriminal (กองทะเบียนประวัติอาชญากร) Kepolisian Kerajaan Thailand pada kop resmi RTP dengan tanda tangan basah dan segel hologram. Type 1 menyatakan dalam satu baris apakah pemohon punya catatan pada database pusat RTP; Type 2 memerinci setiap kasus termasuk yang dibatalkan, mendapat pengampunan, atau dihapus, beserta tanggal putusan.",
          "Identitas pemohon diverifikasi lewat kartu sidik jari roll 10 jari yang diambil langsung di Pathum Wan, atau bagi pemohon luar negeri di kantor polisi setempat lalu dikirim ke Bangkok. Nama harus sama persis karakter demi karakter dengan paspor; satu huruf berbeda pun akan ditolak kedutaan penerima.",
          "Biaya pemerintah 100 baht per lembar. Biaya jasa kami mencakup antre, perwakilan, escort, QC, koordinasi terjemahan dan kurir — dikutip di muka dengan harga tetap.",
        ],
      },
      {
        heading: "Cara mengajukan dari luar negeri tanpa terbang ke Thailand",
        paragraphs: [
          "Kami kirimkan template POA bilingual (Thai-Indonesia atau Thai-Inggris) yang dirancang khusus untuk Divisi Catatan Kriminal RTP. Anda menandatanganinya di hadapan pejabat konsuler kedutaan Thailand di negara Anda, atau (di negara Konvensi Den Haag) di hadapan notaris lokal lalu di-apostille oleh kementerian negara bagian.",
          "Selanjutnya Anda ambil sidik jari di kantor polisi lokal menggunakan format kartu kompatibel AFIS yang kami kirim (atau formulir pemindaian tanpa tinta untuk yurisdiksi tertentu). POA yang telah ditandatangani, kartu sidik jari, salinan halaman biodata paspor, visa atau izin tinggal, dikirim melalui kurir internasional ke kantor Bangkok kami.",
          "Petugas kami mengajukan atas nama Anda di Pathum Wan, memantau nomor antrean setiap hari, mengambil sertifikat begitu terbit, dan mengirim via DHL Express atau EMS tercatat ke alamat luar negeri Anda. Rata-rata 15-25 hari kerja dari awal hingga akhir.",
        ],
      },
      {
        heading: "Terjemahan, legalisasi Kemlu dan legalisasi kedutaan",
        paragraphs: [
          "Untuk negara tujuan yang menerima naskah asli Thai berpasangan dengan terjemahan tersertifikasi, tim terjemah internal kami menghasilkan versi Inggris (atau NAATI, Korea, Jepang, Tionghoa, Jerman) lengkap dengan kredensial penerjemah. Untuk negara yang mensyaratkan Kemlu Thailand melegalkan terjemahan, petugas kami di Chaeng Wattana mengajukan dan mengambil dalam 2-4 hari kerja.",
          "Negara yang mensyaratkan cap kedutaan tambahan — Tiongkok, Arab Saudi, UEA, Taiwan — masuk paket Rantai Legalisasi Penuh. Kami memesan jadwal kedutaan, mengajukan berkas ber-cap Kemlu, membayar biaya konsuler, dan mengembalikan dokumen yang telah dilegalkan penuh, siap diserahkan ke imigrasi atau pemberi kerja negara tujuan.",
        ],
      },
    ],
  },
};
